/**
 * The Grid's live light (#1465, step 2). Built to `threads/live-grid-push/
 * mockup.html` — command-bar placement, chip only, the mockup's words.
 * Corbin, 2026-09-11: "command bar placement, chip only, keep the words."
 *
 * Four states, never a fifth:
 *   Live · updated N s ago            stream open; N = time since the board
 *                                     last re-read for ANY reason
 *   Live · N changes · just now       a push landed (dot pulses 3×, ~4 s)
 *   Reconnecting · checking every 15 s   stream dropped, browser retrying
 *   Not live · checking every 15 s    stream off; polling floor still runs
 *
 * Amber and grey mean "slower", never "broken": the #1464 polling floor is
 * always underneath. No names (the server sends counts, not people), no
 * sound, no toast, no red.
 */
import { useEffect, useState } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { SseStatus } from "@/lib/use-sse";

export interface LiveChipProps {
    status: SseStatus;
    /** Server time of the last change hint acted on, or null. */
    lastChangeAt: Date | null;
    /** Rows that hint covered. */
    lastChangeCount: number;
    /** Per-table counts from that hint, for the hover. */
    lastChangeTables?: Record<string, number>;
    /** Epoch ms of the board's last successful read (any reason), or null. */
    lastReadAt: number | null;
}

/** How long a push keeps the "N changes · just now" face. */
export const CHANGED_HOLD_MS = 4_000;

export type LiveChipState = "live" | "changed" | "reconnecting" | "off";

export function resolveChipState(status: SseStatus, lastChangeAt: Date | null, now: number): LiveChipState {
    if (status === "live") {
        return lastChangeAt && now - lastChangeAt.getTime() < CHANGED_HOLD_MS ? "changed" : "live";
    }
    if (status === "reconnecting") return "reconnecting";
    return "off";
}

/** "just now" · "4 s ago" · "1 min ago" — the mockup's counter. */
export function formatAgo(sinceMs: number): string {
    if (sinceMs < 2_000) return "just now";
    if (sinceMs < 60_000) return `${Math.floor(sinceMs / 1000)} s ago`;
    const m = Math.floor(sinceMs / 60_000);
    return `${m} min ago`;
}

const TABLE_WORDS: Record<string, [string, string]> = {
    jobs: ["job", "jobs"],
    job_assignments: ["crew change", "crew changes"],
    job_appointments: ["appointment", "appointments"],
    dispatch_day_pairings: ["pairing", "pairings"],
    user_day_unavailability: ["out-day", "out-days"],
};

/** {jobs: 2, job_assignments: 1} → "2 jobs, 1 crew change". */
export function describeTables(tables: Record<string, number> | undefined): string {
    if (!tables) return "";
    const parts: string[] = [];
    for (const [t, n] of Object.entries(tables)) {
        if (!n) continue;
        const w = TABLE_WORDS[t] ?? [t.replace(/_/g, " "), t.replace(/_/g, " ")];
        parts.push(`${n} ${n === 1 ? w[0] : w[1]}`);
    }
    return parts.join(", ");
}

/** The chip's two words and its hover, for one state. Pure, so it is tested. */
export function describeChip(
    state: LiveChipState,
    p: {
        lastChangeAt: Date | null;
        lastChangeCount: number;
        lastChangeTables?: Record<string, number>;
        lastReadAt: number | null;
    },
    now: number,
): { word: string; detail: string; hover: string } {
    switch (state) {
        case "changed": {
            const n = p.lastChangeCount;
            const at = p.lastChangeAt
                ? p.lastChangeAt.toLocaleTimeString(undefined, {
                      hour: "numeric",
                      minute: "2-digit",
                      second: "2-digit",
                  })
                : "";
            const what = describeTables(p.lastChangeTables);
            return {
                word: "Live",
                detail: `${n} ${n === 1 ? "change" : "changes"} · just now`,
                hover: `${n} ${n === 1 ? "change" : "changes"} landed${at ? ` at ${at}` : ""}${what ? ` — ${what}` : ""}. The board has already been refreshed.`,
            };
        }
        case "live": {
            const since = p.lastReadAt ? now - p.lastReadAt : 0;
            const ago = formatAgo(since);
            const secs = Math.max(0, Math.floor(since / 1000));
            return {
                word: "Live",
                detail: `updated ${ago}`,
                hover: `Live. The board updates the moment anyone changes it. Last read ${secs < 2 ? "just now" : `${secs} seconds ago`}.`,
            };
        }
        case "reconnecting":
            return {
                word: "Reconnecting",
                detail: "checking every 15 s",
                hover: "The live connection dropped and is reconnecting. Until then the board re-checks every 15 seconds. You do not need to do anything.",
            };
        case "off":
        default:
            return {
                word: "Not live",
                detail: "checking every 15 s",
                hover: "Live updates are off in this tab. The board still re-checks every 15 seconds. Reload the page to reconnect.",
            };
    }
}

const DOT: Record<LiveChipState, string> = {
    live: "bg-emerald-400",
    changed: "bg-emerald-400 live-chip-pulse",
    reconnecting: "bg-amber-400",
    off: "bg-stone-400",
};

export function LiveChip(props: LiveChipProps) {
    // A 1 s tick so "updated N s ago" counts up and the "just now" face
    // settles back on its own. The interval is cheap and only runs while the
    // command bar is mounted.
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);
    // A fresh hint must flip the face immediately, not on the next tick.
    useEffect(() => {
        setNow(Date.now());
    }, [props.lastChangeAt, props.status]);

    const state = resolveChipState(props.status, props.lastChangeAt, now);
    const { word, detail, hover } = describeChip(state, props, now);

    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span
                        data-testid="grid-live-chip"
                        data-state-live={state}
                        tabIndex={0}
                        aria-label={`${word}, ${detail}`}
                        className="inline-flex h-7 shrink-0 cursor-default items-center gap-1.5 whitespace-nowrap rounded-full border border-shell-foreground/15 bg-shell-foreground/10 pl-2.5 pr-2.5 text-xs font-semibold text-shell-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                    >
                        <span aria-hidden className={cn("h-2 w-2 shrink-0 rounded-full", DOT[state])} />
                        <span>{word}</span>
                        <span className="font-medium tabular-nums text-shell-foreground/60">{detail}</span>
                    </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-xs leading-snug">
                    {hover}
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
